import type { Project } from 'models/project'
import { ProjectTypes } from 'constants/enums'
import { toChartPoints, type StockMarketBar } from 'models/chart'
import { latestDate } from './shared'
import type { ProductChart } from './types'

/** Vendor ids as the price service sends them, to the names shown under the chart. */
const SOURCE_NAMES: Record<string, string> = {
  polygon: 'Polygon.io',
  tradingview: 'TradingView',
  eodhd: 'EODHD',
  eulerpool: 'Eulerpool',
}

/**
 * TEMPORARY: the price service keys some stocks by the vendor's ticker, not the project's
 * `underlyingSymbol`. Delete once the backend resolves the symbol itself.
 */
const PRICE_TICKERS: Record<string, string> = {
  '3350': 'MTPLF',
  ALCPB: 'ALCPB.PA',
  H100: 'H100.ST',
  STRC: 'STRC.US',
}

/** Ticker to request prices for; the project's own symbol stays the one shown. */
export const priceTicker = (symbol: string): string => PRICE_TICKERS[symbol.toUpperCase()] ?? symbol

const sourceName = (provider: string | undefined): string | undefined => {
  const id = provider?.trim()
  if (!id) return undefined
  return SOURCE_NAMES[id.toLowerCase()] ?? id
}

/** Equity: the underlying stock's daily close, with its logo when back-office has one. */
export const toUnderlyingStockChart = (
  project: Pick<Project, 'type' | 'underlyingSymbol'> | undefined,
  bars: StockMarketBar[] | undefined,
  underlyingIcon?: string,
): ProductChart | undefined => {
  const symbol = project?.underlyingSymbol?.trim()
  if (project?.type !== ProjectTypes.EQUITY || !symbol) return undefined
  const points = toChartPoints(
    bars ?? [],
    (bar) => bar.time,
    (bar) => bar.close,
  )
  return {
    kind: 'stock',
    symbol,
    labelKey: 'stoPage.heroChart.underlying',
    denominations: [{ currency: bars?.find((bar) => bar.currency)?.currency ?? 'USD', points }],
    asOf: latestDate(points),
    // The vendor of the newest bar that names one, by date: the API's order is not relied on.
    source: sourceName(
      [...(bars ?? [])]
        .filter((bar) => bar.provider)
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())[0]?.provider,
    ),
    icon: underlyingIcon?.trim() || undefined,
  }
}
